"use client"

import * as React from "react"

import type { Product } from "@/stores/app-store"
import {
  isSurveysOnboardingComplete,
  loadSurveysOnboardingCompleted,
  loadSurveysOnboardingGuideDismissed,
  loadSurveysOnboardingNextTasksDismissed,
  loadSurveysOnboardingPath,
  resolveSurveysOnboardingOperationsStage,
  saveSurveysOnboardingCompleted,
  saveSurveysOnboardingGuideDismissed,
  saveSurveysOnboardingNextTasksDismissed,
  saveSurveysOnboardingPath,
  surveysDashboardOnboardingSteps,
  type SurveysOnboardingPath,
} from "@/lib/evaluation-dashboard-onboarding"
import {
  setEvaluationDashboardScenario,
  useEvaluationDashboardScenario,
} from "@/hooks/use-evaluation-dashboard-scenario"

function readOnboardingState() {
  const path = loadSurveysOnboardingPath()
  return {
    path,
    completed: loadSurveysOnboardingCompleted(path ?? "manual"),
    setupDismissed: loadSurveysOnboardingGuideDismissed(),
    nextTasksDismissed: loadSurveysOnboardingNextTasksDismissed(),
  }
}

export function useSurveysFirstTermSetup(product: Product) {
  const scenario = useEvaluationDashboardScenario()
  const initial = React.useMemo(() => readOnboardingState(), [])
  const [path, setPathState] = React.useState<SurveysOnboardingPath | null>(initial.path)
  const [completed, setCompletedState] = React.useState<Set<number>>(initial.completed)
  const [setupDismissed, setSetupDismissed] = React.useState(initial.setupDismissed)
  const [nextTasksDismissed, setNextTasksDismissed] = React.useState(
    initial.nextTasksDismissed,
  )

  React.useEffect(() => {
    if (scenario !== "onboarding") return
    const next = readOnboardingState()
    setPathState(next.path)
    setCompletedState(next.completed)
    setSetupDismissed(next.setupDismissed)
    setNextTasksDismissed(next.nextTasksDismissed)
  }, [scenario])

  const steps = React.useMemo(
    () => surveysDashboardOnboardingSteps(product, path ?? "manual"),
    [path, product],
  )

  const isChecklistComplete = isSurveysOnboardingComplete(completed, steps.length)

  const operationsStage = resolveSurveysOnboardingOperationsStage(completed, steps, {
    checklistComplete: isChecklistComplete,
    setupDismissed,
    nextTasksDismissed,
    scenario,
  })

  const showSetupGuide = scenario === "onboarding" && !setupDismissed
  const showNextTasks =
    scenario === "onboarding" && isChecklistComplete && !nextTasksDismissed
  const showOnboardingLayer = showSetupGuide || showNextTasks

  const setPath = React.useCallback((next: SurveysOnboardingPath) => {
    setPathState(next)
    saveSurveysOnboardingPath(next)
  }, [])

  const setCompleted = React.useCallback(
    (next: Set<number>) => {
      setCompletedState(next)
      if (path) saveSurveysOnboardingCompleted(path, next)
    },
    [path],
  )

  const dismissSetupGuide = React.useCallback(() => {
    setSetupDismissed(true)
    saveSurveysOnboardingGuideDismissed(true)
  }, [])

  const dismissNextTasks = React.useCallback(() => {
    setNextTasksDismissed(true)
    saveSurveysOnboardingNextTasksDismissed(true)
    if (scenario === "onboarding") {
      setEvaluationDashboardScenario("midway")
    }
  }, [scenario])

  return {
    path,
    setPath,
    steps,
    completed,
    setCompleted,
    isChecklistComplete,
    operationsStage,
    showSetupGuide,
    showNextTasks,
    showOnboardingLayer,
    dismissSetupGuide,
    dismissNextTasks,
  }
}
